/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { StyleProps } from 'src/lib/styleProps';
import { FormProvider, useForm } from 'react-hook-form';
import { media } from 'src/common/styles/breakpoints';
import { useHistory } from 'react-router-dom';
import { ConsentListSkeleton } from 'src/pages/MainPage/components/ConsentForm/ConsentListSkeleton';
import {
  ConsentParams,
  mapToConsentItem,
} from 'src/pages/MainPage/components/ConsentForm/consentModels';
import { statuteAndPrivacyPolicyConsent } from '@tenant/pages/MainPage/components/ConsentForm/ConsentForm.constants';
import { omit } from 'ramda';
import {
  useAcceptConsentsMutation,
  useConsentsQuery,
} from 'src/pages/MainPage/components/ConsentForm/consentQueries';
import { useStatusOfVerificationProcessQuery } from 'src/common/components/RequireCorrectStep/useProcessStatusQuery';
import { useVerificationId } from 'src/common/hooks/useVerificationId';
import { FormattedMessage } from 'react-intl';
import MainPageConsentsHeader from '@tenant/components/MainPageConsentsHeader';
import MainPageCheckAllConsent from '@tenant/components/MainPageCheckAllConsent';
import MainPageConsentItem from '@tenant/components/MainPageConsentItem';
import MainPageConsentsContainer from '@tenant/components/MainPageConsentsContainer';
import MainPageConsentList from '@tenant/components/MainPageConsentList';
import MainPageConsentsInfo from '@tenant/components/MainPageConsentsInfo';
import MainPageNextButton from '@tenant/components/MainPageNextButton';

const ConsentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 -0.5rem;
  ${media.w.min.px576(css`
    margin: 0;
  `)};
`;

const checkAllId = 'checkAll';
interface ConsentFormData {
  [key: number]: boolean;
}

const prepareConsentFormToSend = (data: ConsentFormData): ConsentParams[] =>
  Object.keys(data).map((key) => ({
    processBusinessConsentId: Number(key),
    isChecked: data[Number(key)],
  }));

const staticConsents = [statuteAndPrivacyPolicyConsent];

export const ConsentFormSection: FC<StyleProps> = ({ className, style }) => {
  const verificationId = useVerificationId();
  const history = useHistory();

  if (!verificationId) {
    history.replace('404');
  }

  const methods = useForm({});
  const processStatus = useStatusOfVerificationProcessQuery(verificationId);

  const consents = useConsentsQuery(verificationId);
  const mutation = useAcceptConsentsMutation();

  const allConsents = useMemo(
    () =>
      consents.data
        ? [
            statuteAndPrivacyPolicyConsent,
            ...consents.data.map((consent) => mapToConsentItem(consent)),
          ]
        : staticConsents,
    [consents.data]
  );

  const { handleSubmit, setValue, watch, trigger } = methods;

  const onSubmit = (formRawData: ConsentFormData) => {
    const consentValues = prepareConsentFormToSend(formRawData);
    const filteredValues = consentValues.filter((item) =>
      consents.data
        ?.map((consent) => consent.id)
        .includes(item.processBusinessConsentId)
    );
    mutation.mutate(
      {
        businessConsent: filteredValues,
        verificationId: verificationId!,
      },
      {
        onSuccess: () => {
          processStatus.refetch();
        },
      }
    );
  };

  useEffect(() => {
    const subscription = watch((formValues) => {
      const allChecked = !Object.values(
        omit([checkAllId], formValues)
      ).includes(false);

      if (formValues[checkAllId] && !allChecked) {
        setValue(checkAllId, false);
      }

      if (!formValues[checkAllId] && allChecked) {
        setValue(checkAllId, true);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <MainPageConsentsContainer>
      <MainPageConsentsHeader />
      <FormProvider {...methods}>
        <ConsentForm onSubmit={handleSubmit(onSubmit)}>
          <div>
            {consents.isLoading || processStatus.isLoading ? (
              <MainPageConsentList>
                <ConsentListSkeleton />
              </MainPageConsentList>
            ) : (
              <MainPageConsentList>
                <MainPageCheckAllConsent
                  key={checkAllId}
                  consent={{
                    id: checkAllId,
                    mandatory: false,
                    label: (
                      <strong>
                        <FormattedMessage id="main.consents.checkAll" />
                      </strong>
                    ),
                  }}
                  handleChange={(value) => {
                    allConsents.forEach((consent) => {
                      setValue(`${consent.id}` as `${string}`, value);
                      trigger(`${consent.id}` as `${string}`);
                    });
                  }}
                />
                {allConsents.map((consent) => (
                  <MainPageConsentItem key={consent.id} consent={consent} />
                ))}
              </MainPageConsentList>
            )}
          </div>
          <MainPageConsentsInfo />
          <MainPageNextButton
            disabled={
              mutation.isLoading ||
              consents.isLoading ||
              processStatus.isLoading
            }
            type="submit"
          >
            <FormattedMessage id="common.next" />
          </MainPageNextButton>
        </ConsentForm>
      </FormProvider>
    </MainPageConsentsContainer>
  );
};
